import { gql } from "@apollo/client";
import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";

import type { 
  UpdatePassword, 
  UpdatePasswordResp, 
  UpdatePasswordType, 
  UpdatePasswordValues 
} from "../../types/updatePasswordTypes";

const UPDATE_PASSWORD = gql`
  mutation UpdatePassword($attributes: UpdatePasswordInput!) {
    updatePassword(attributes: $attributes) {
      message
      success
      errors
    }
  }
`;

type UseUpdatePasswordOptions = {
  onCompleted?: (updatedPassword: UpdatePasswordType) => void;
  onError?: (error: unknown) => void;
  mutationOptions?: Parameters<typeof useMutation<UpdatePasswordResp, UpdatePasswordValues>>[1];
};

const useUpdatePassword = (options?: UseUpdatePasswordOptions) => {
  const [mutate, { data, loading, error }] = useMutation<
    UpdatePasswordResp,
    UpdatePasswordValues
  >(UPDATE_PASSWORD, options?.mutationOptions);

  const updatePassword = useCallback(
    async (input: UpdatePassword) => {
      try {
        const result = await mutate({
          variables: {
            attributes: {
              id: input.id!,
              currentPassword: input.currentPassword,
              password: input.password,
              passwordConfirmation: input.passwordConfirmation,
            },
          },
        });

        const updatePassword = result?.data?.updatePassword;
        
        if (updatePassword?.errors?.length) {
          if (updatePassword.errors.includes("current_password")) {
            throw new Error(updatePassword.message);
          }
          throw new Error(updatePassword.errors.join(", "));
        }

        if (options?.onCompleted && updatePassword) {
          options.onCompleted(updatePassword);
        }

        return updatePassword;
      } catch (err) {
        if (options?.onError) {
          options.onError(err);
        }
        throw err;
      }
    },
    [mutate, options]
  );

  const password = data?.updatePassword;

  return {
    updatePassword,
    updatedPassword: password,
    loading,
    error: error as Error | null | undefined,
  };
}

export default useUpdatePassword
